import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { superadminApi, type BackgroundJobLogDto, type EmailLogDto, type JobSummaryDto } from '@/api/superadmin'
import { Activity, Mail, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Clock, RefreshCw, Play, Calendar, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

type Tab = 'jobs' | 'emails'

const JOB_NAMES = ['ExpiryReminder', 'BookingReminder', 'WeeklyDigest', 'InvoiceAlert']

const EMAIL_CATEGORIES = [
  'certificate_issued', 'attendee_certificate', 'attendee_magic_link', 'client_manager_magic_link',
  'expiry_reminder', 'expiry_alert_admin', 'expiry_reminder_client_manager',
  'booking_confirmation', 'booking_reminder', 'booking_enquiry',
  'invoice', 'invoice_alert', 'weekly_digest', 'welcome', 'invitation',
  'password_reset', 'contact_enquiry',
]

function fmt(date: string) {
  return new Date(date).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function fmtRelative(date: string) {
  const d = new Date(date)
  const diffMs = Date.now() - d.getTime()
  const diffMins = Math.floor(diffMs / 60_000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

function duration(ms: number | null) {
  if (ms === null) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function StatusBadge({ status }: { status: BackgroundJobLogDto['status'] }) {
  if (status === 'Succeeded') return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
      <CheckCircle2 size={12} /> Succeeded
    </span>
  )
  if (status === 'Failed') return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
      <XCircle size={12} /> Failed
    </span>
  )
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
      <Clock size={12} /> Running
    </span>
  )
}

// ── Job summary cards ──────────────────────────────────────────────────────────

function JobCard({ job, onTrigger }: { job: JobSummaryDto; onTrigger: () => void }) {
  const last = job.lastRun
  const isRunning = last?.status === 'Running'

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-slate-800 text-sm">{job.jobName}</span>
            <span className="inline-flex items-center gap-1 text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
              <Calendar size={10} />
              {job.schedule}
            </span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">{job.description}</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onTrigger}
          disabled={isRunning}
          className="gap-1.5 shrink-0"
        >
          {isRunning
            ? <><RefreshCw size={13} className="animate-spin" /> Running</>
            : <><Play size={13} /> Run now</>
          }
        </Button>
      </div>

      <div className="border-t border-slate-100 pt-3">
        {last ? (
          <div className="flex items-center gap-3 flex-wrap">
            <StatusBadge status={last.status} />
            <span className="text-xs text-slate-500" title={fmt(last.startedAt)}>
              {fmtRelative(last.startedAt)}
            </span>
            {last.durationMs !== null && (
              <span className="text-xs text-slate-400">{duration(last.durationMs)}</span>
            )}
            {last.recordsProcessed !== null && (
              <span className="text-xs text-slate-400">{last.recordsProcessed} records</span>
            )}
            {last.errorMessage && (
              <span className="inline-flex items-center gap-1 text-xs text-red-600" title={last.errorMessage}>
                <AlertCircle size={11} />
                <span className="truncate max-w-[240px]">{last.errorMessage}</span>
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-slate-400">No runs recorded</span>
        )}
      </div>
    </div>
  )
}

// ── Jobs tab ───────────────────────────────────────────────────────────────────

function JobsTab() {
  const [jobName, setJobName] = useState('')
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 50
  const { data: summaries, refetch: refetchSummary } = useQuery({
    queryKey: ['superadmin-job-summary'],
    queryFn: () => superadminApi.getJobSummary().then(r => r.data),
    refetchInterval: 10_000,
  })

  const triggerMutation = useMutation({
    mutationFn: (name: string) => superadminApi.triggerJob(name),
    onSuccess: () => {
      // Poll until the Running status appears
      setTimeout(() => {
        refetchSummary()
        refetch()
      }, 800)
    },
  })

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['superadmin-jobs', jobName, page],
    queryFn: () => superadminApi.getJobLog({ jobName: jobName || undefined, page, pageSize: PAGE_SIZE }).then(r => r.data),
    refetchInterval: 10_000,
  })

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1

  return (
    <div className="space-y-6">
      {/* Job cards */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Jobs</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {summaries
            ? summaries.map(job => (
                <JobCard
                  key={job.jobName}
                  job={job}
                  onTrigger={() => triggerMutation.mutate(job.jobName)}
                />
              ))
            : JOB_NAMES.map(n => (
                <div key={n} className="bg-white border border-slate-200 rounded-xl p-4 animate-pulse h-28" />
              ))}
        </div>
      </div>

      {/* Run history */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Run history</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <select
              value={jobName}
              onChange={e => { setJobName(e.target.value); setPage(1) }}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="">All jobs</option>
              {JOB_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={() => { refetch(); refetchSummary() }} className="gap-2">
              <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
              Refresh
            </Button>
            {data && (
              <span className="text-sm text-slate-500 ml-auto">{data.total} runs</span>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Job</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Started</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Records</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Duration</th>
                </tr>
              </thead>
              <tbody>
                {!data || data.items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">
                      No job runs recorded yet.
                    </td>
                  </tr>
                ) : data.items.map((job: BackgroundJobLogDto) => (
                  <tr key={job.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-800">{job.jobName}</td>
                    <td className="px-4 py-3 text-slate-500">{fmt(job.startedAt)}</td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <StatusBadge status={job.status} />
                        {job.errorMessage && (
                          <p className="text-xs text-red-600 max-w-xs truncate" title={job.errorMessage}>
                            {job.errorMessage}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {job.recordsProcessed ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-500">
                      {duration(job.durationMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
                <ChevronRight size={16} />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Emails tab ─────────────────────────────────────────────────────────────────

function EmailsTab() {
  const [category, setCategory] = useState('')
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 50

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['superadmin-emails', category, page],
    queryFn: () => superadminApi.getEmailLog({ category: category || undefined, page, pageSize: PAGE_SIZE }).then(r => r.data),
  })

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select
          value={category}
          onChange={e => { setCategory(e.target.value); setPage(1) }}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
        >
          <option value="">All categories</option>
          {EMAIL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </Button>
        {data && (
          <span className="text-sm text-slate-500 ml-auto">{data.total} emails</span>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="text-left px-4 py-3 font-medium text-slate-600">To</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Category</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Subject</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Sent</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Result</th>
            </tr>
          </thead>
          <tbody>
            {!data || data.items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">
                  No emails logged yet.
                </td>
              </tr>
            ) : data.items.map((email: EmailLogDto) => (
              <tr key={email.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                <td className="px-4 py-3 text-slate-700 font-mono text-xs">{email.toEmail}</td>
                <td className="px-4 py-3">
                  <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                    {email.category}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600 max-w-xs truncate" title={email.subject}>
                  {email.subject}
                </td>
                <td className="px-4 py-3 text-slate-500">{fmt(email.sentAt)}</td>
                <td className="px-4 py-3">
                  {email.success ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                      <CheckCircle2 size={12} /> Sent
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700" title={email.errorMessage ?? undefined}>
                      <XCircle size={12} /> Failed
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
            <ChevronRight size={16} />
          </Button>
        </div>
      )}
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function MonitoringPage() {
  const [tab, setTab] = useState<Tab>('jobs')

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Monitoring</h1>
        <p className="text-sm text-slate-500 mt-1">Background job runs and email delivery log.</p>
      </div>

      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setTab('jobs')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'jobs' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Activity size={15} />
          Background jobs
        </button>
        <button
          onClick={() => setTab('emails')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'emails' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Mail size={15} />
          Email log
        </button>
      </div>

      {tab === 'jobs' && <JobsTab />}
      {tab === 'emails' && <EmailsTab />}
    </div>
  )
}
